// Canonical Bobcat Duration parsing/formatting. This is the JavaScript half of
// the shared Duration concept; the Ruby half lives in
// app/services/durations/duration.rb and MUST stay behaviorally equivalent.
//
// CANONICAL DURATION SPEC (one syntax, one set of rules, both languages):
//   Units: d = 86400s, h = 3600s, m = 60s. There is no seconds unit.
//   Canonical string: the non-zero d/h/m components, in that order, joined by a
//     single space, each "<n><unit>" -- e.g. "2d 3h 15m", "1d 6h", "1h 30m",
//     "30m", "1h". A zero total formats as "0m".
//   Parsing: case-insensitive; all whitespace is ignored ("2h30m", "2h 30m",
//     " 1d 6h " are equal); the d, h, m components appear in that order, each
//     optional and at most once; counts are non-negative integers that MAY
//     exceed the usual range (e.g. "90m") and are re-normalized on format
//     ("90m" -> "1h 30m", "25h" -> "1d 1h"); the total must be positive, else
//     the input is invalid. Granularity is minutes.
//   Invalid input (empty, unknown units/characters, out-of-order, non-positive
//     total) returns null. Callers surface a validation error and MUST NOT
//     silently guess or auto-correct.
//
// Authored as CommonJS so it is unit-testable with Node's built-in test runner
// and importable by the Vue components via the webpack/babel ESM interop.

const SECONDS_PER_DAY = 86400;
const SECONDS_PER_HOUR = 3600;
const SECONDS_PER_MINUTE = 60;
const MINUTES_PER_DAY = 24 * 60;
const PARSE_REGEX = /^(?:(\d+)d)?(?:(\d+)h)?(?:(\d+)m)?$/;

// Parse a duration string to a positive integer number of seconds, or null.
function parse(input) {
  if (input === null || input === undefined) return null;

  const compact = String(input).trim().replace(/\s+/g, "").toLowerCase();
  if (compact === "") return null;

  const match = compact.match(PARSE_REGEX);
  if (!match) return null;

  const total =
    parseInt(match[1] || 0, 10) * SECONDS_PER_DAY +
    parseInt(match[2] || 0, 10) * SECONDS_PER_HOUR +
    parseInt(match[3] || 0, 10) * SECONDS_PER_MINUTE;

  return total > 0 ? total : null;
}

// Format a number of seconds into the canonical string. Truncates to whole
// minutes; non-positive input formats as "0m".
function format(seconds) {
  const secs = Math.trunc(Number(seconds) || 0);
  if (secs <= 0) return "0m";

  const minutes = Math.floor(secs / SECONDS_PER_MINUTE);
  const days = Math.floor(minutes / MINUTES_PER_DAY);
  const rem = minutes % MINUTES_PER_DAY;
  const hours = Math.floor(rem / 60);
  const mins = rem % 60;

  const parts = [];
  if (days > 0) parts.push(`${days}d`);
  if (hours > 0) parts.push(`${hours}h`);
  if (mins > 0) parts.push(`${mins}m`);

  return parts.length ? parts.join(" ") : "0m";
}

function isValid(input) {
  return parse(input) !== null;
}

module.exports = { parse, format, isValid };
